import { useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Empty,
  Modal,
  Result,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import type {
  CreateFleetBody,
  FleetDriver,
  FleetVehicle,
  UpdateFleetBody,
} from '@goelectric/shared-types';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/features/auth/context/AuthContext';
import { ApiError } from '@/lib/http/apiClient';
import { canWriteFlotas } from '@/layouts/navigation';
import { fleetApi } from '../api/fleetApi';
import { DriverFormModal } from '../components/DriverFormModal';
import { FleetAccountCard } from '../components/FleetAccountCard';
import { FleetStatusBadge } from '../components/FleetBadges';
import { FleetDriversTable } from '../components/FleetDriversTable';
import { FleetForm, fleetToFormState } from '../components/FleetForm';
import { FleetVehiclesTable } from '../components/FleetVehiclesTable';
import { VehicleFormModal } from '../components/VehicleFormModal';
import { useFleet } from '../hooks/useFleet';
import { useFleetDrivers } from '../hooks/useFleetDrivers';
import { useFleetVehicles } from '../hooks/useFleetVehicles';
import { describeFleetApiError } from '../utils/fleetErrors';
import {
  FLEET_INACTIVE_HINT,
  FLEET_RECONCILIATION_MESSAGE,
  requiresFleetReconciliation,
} from '../utils/fleetMessages';
import {
  FLEET_BILLING_MODE_LABELS,
  FLEET_DOCUMENT_TYPE_LABELS,
  formatDateTime,
  formatFleetDocument,
} from '../utils/format';

interface DriverModalState {
  open: boolean;
  driver: FleetDriver | null;
}

interface VehicleModalState {
  open: boolean;
  vehicle: FleetVehicle | null;
}

const CLOSED_DRIVER_MODAL: DriverModalState = { open: false, driver: null };
const CLOSED_VEHICLE_MODAL: VehicleModalState = { open: false, vehicle: null };

/**
 * Detalle de Flota (US-2.3, `/flotas/:fleetId`).
 *
 * Cabecera con nombre/código/estado y edición de la ficha; Cuenta Maestra con saldo real
 * y ajuste auditable reutilizando el modal de Clientes; secciones de conductores y
 * vehículos RFID con alta/edición/desvincular/inactivar. Consumo y facturación quedan como
 * placeholders honestos (US 2.9/2.5), sin inventar datos.
 */
export function FleetDetailPage() {
  const { fleetId } = useParams<{ fleetId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { message, modal } = App.useApp();
  const { fleet, loading, error, notFound, refresh: refreshFleet } = useFleet(fleetId);
  const drivers = useFleetDrivers(fleetId);
  const vehicles = useFleetVehicles(fleetId);
  const canWrite = canWriteFlotas(user?.role);
  const fleetInactive = fleet?.status === 'INACTIVE';

  const [editOpen, setEditOpen] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [driverModal, setDriverModal] = useState<DriverModalState>(CLOSED_DRIVER_MODAL);
  const [vehicleModal, setVehicleModal] = useState<VehicleModalState>(CLOSED_VEHICLE_MODAL);

  const goToList = () => navigate('/flotas');

  const openDriverModal = (driver: FleetDriver | null) =>
    setDriverModal({ open: true, driver });
  const openVehicleModal = (vehicle: FleetVehicle | null) =>
    setVehicleModal({ open: true, vehicle });

  /** Refresca tabla y contadores de la flota tras cambiar conductores/vehículos. */
  const refreshPeople = (kind: 'drivers' | 'vehicles') => {
    if (kind === 'drivers') drivers.refresh();
    else vehicles.refresh();
    refreshFleet();
  };

  const handleEdit = async (body: CreateFleetBody | UpdateFleetBody) => {
    if (!fleetId) return;
    setEditSubmitting(true);
    setEditError(null);
    try {
      await fleetApi.update(fleetId, body as UpdateFleetBody);
      message.success('Flota actualizada.');
      setEditOpen(false);
      refreshFleet();
    } catch (caught) {
      setEditError(describeFleetApiError(caught, 'No se pudo actualizar la flota.').message);
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleUnlinkDriver = (driver: FleetDriver) => {
    if (!fleetId) return;
    modal.confirm({
      title: 'Desvincular conductor',
      content: `${driver.fullName} dejará de pertenecer a la flota. El cliente no se elimina y conserva su historial.`,
      okText: 'Desvincular',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await fleetApi.unlinkDriver(fleetId, driver.customerId);
          message.success('Conductor desvinculado de la flota.');
          refreshPeople('drivers');
        } catch (caught) {
          message.error(
            describeFleetApiError(caught, 'No se pudo desvincular el conductor.').message,
          );
        }
      },
    });
  };

  const handleDeactivateVehicle = (vehicle: FleetVehicle) => {
    if (!fleetId) return;
    modal.confirm({
      title: 'Inactivar vehículo',
      content: `${vehicle.plate} quedará inactivo y su tarjeta RFID se bloqueará en los tenants de CitrineOS. El historial se conserva.`,
      okText: 'Inactivar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await fleetApi.deactivateVehicle(fleetId, vehicle.id);
          message.success('Vehículo inactivado.');
          refreshPeople('vehicles');
        } catch (caught) {
          message.error(
            describeFleetApiError(caught, 'No se pudo inactivar el vehículo.').message,
          );
          // 502 = falló el bloqueo cross-tenant y el vehículo sigue activo: refrescamos
          // la tabla para no dejar en pantalla un estado optimista que no ocurrió.
          if (caught instanceof ApiError && caught.status === 502) {
            refreshPeople('vehicles');
          }
        }
      },
    });
  };

  const handleReactivateVehicle = async (vehicle: FleetVehicle) => {
    if (!fleetId) return;
    if (fleetInactive) {
      message.warning(FLEET_INACTIVE_HINT);
      return;
    }
    try {
      const updated = await fleetApi.updateVehicle(fleetId, vehicle.id, {
        status: 'ACTIVE',
      });
      // La reactivación es fail-safe: si falla la replicación cross-tenant el backend
      // responde 200 con `authorizationStatus` PARTIAL/ERROR en lugar de lanzar 502.
      if (requiresFleetReconciliation(updated.authorizationStatus)) {
        message.warning(FLEET_RECONCILIATION_MESSAGE);
      } else {
        message.success('Vehículo reactivado.');
      }
      // Refrescamos siempre para reflejar el estado real de autorización del vehículo.
      refreshPeople('vehicles');
    } catch (caught) {
      message.error(
        describeFleetApiError(caught, 'No se pudo reactivar el vehículo.').message,
      );
      // Ante un fallo inesperado refrescamos para no dejar un estado optimista en pantalla.
      refreshPeople('vehicles');
    }
  };

  if (loading) {
    return <Card loading title="Flota" />;
  }

  if (notFound) {
    return (
      <Result
        status="404"
        title="Flota no encontrada"
        subTitle="La flota no existe o fue eliminada."
        extra={<Button onClick={goToList}>Volver al listado</Button>}
      />
    );
  }

  if (error || !fleet) {
    return (
      <Result
        status="error"
        title="No se pudo cargar la flota"
        subTitle={error ?? undefined}
        extra={
          <Space>
            <Button type="primary" onClick={refreshFleet}>
              Reintentar
            </Button>
            <Button onClick={goToList}>Volver al listado</Button>
          </Space>
        }
      />
    );
  }

  const reconciliationIssues = vehicles.data.filter(
    (vehicle) =>
      vehicle.authorizationStatus === 'PARTIAL' || vehicle.authorizationStatus === 'ERROR',
  );
  const hasReconciliationError = reconciliationIssues.some(
    (vehicle) => vehicle.authorizationStatus === 'ERROR',
  );

  return (
    <div className="flotas-page">
      <div className="flotas-page__header">
        <Space align="center" wrap>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label="Volver al listado de flotas"
            onClick={goToList}
          />
          <Typography.Title level={3} style={{ margin: 0 }}>
            {fleet.name}
          </Typography.Title>
          <FleetStatusBadge status={fleet.status} />
          <Typography.Text
            type="secondary"
            style={{ fontFamily: 'var(--ge-font-mono)' }}
            title="Código de flota"
          >
            {fleet.codigoFlota}
          </Typography.Text>
        </Space>
        {canWrite && (
          <Button icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
            Editar flota
          </Button>
        )}
      </div>

      <FleetAccountCard fleet={fleet} canWrite={canWrite} onAdjusted={refreshFleet} />

      <Card title="Datos de la flota">
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="ID de flota">
            <span style={{ fontFamily: 'var(--ge-font-mono)' }}>{fleet.id}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Cuenta Maestra">
            <span style={{ fontFamily: 'var(--ge-font-mono)' }}>{fleet.masterAccountId}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Documento">
            <span style={{ fontFamily: 'var(--ge-font-mono)' }}>
              {formatFleetDocument(fleet)}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Tipo de documento">
            {fleet.documentType ? FLEET_DOCUMENT_TYPE_LABELS[fleet.documentType] : 'NIT'}
          </Descriptions.Item>
          <Descriptions.Item label="Teléfono">{fleet.phone}</Descriptions.Item>
          <Descriptions.Item label="Email">{fleet.email || '—'}</Descriptions.Item>
          <Descriptions.Item label="Código de flota">
            <span style={{ fontFamily: 'var(--ge-font-mono)' }}>{fleet.codigoFlota}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Facturación">
            {FLEET_BILLING_MODE_LABELS[fleet.billingMode]}
          </Descriptions.Item>
          <Descriptions.Item label="Creado">{formatDateTime(fleet.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="Actualizado">
            {formatDateTime(fleet.updatedAt)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        title="Conductores"
        extra={
          canWrite ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openDriverModal(null)}
            >
              Vincular conductor
            </Button>
          ) : undefined
        }
      >
        <FleetDriversTable
          drivers={drivers.data}
          meta={drivers.meta}
          loading={drivers.loading}
          error={drivers.error}
          canWrite={canWrite}
          onEdit={openDriverModal}
          onUnlink={handleUnlinkDriver}
          onRetry={drivers.refresh}
          onOffsetChange={drivers.setOffset}
        />
      </Card>

      <Card
        title="Vehículos RFID"
        extra={
          canWrite ? (
            <Tooltip title={fleetInactive ? FLEET_INACTIVE_HINT : undefined}>
              <span tabIndex={fleetInactive ? 0 : undefined}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  disabled={fleetInactive}
                  onClick={() => openVehicleModal(null)}
                >
                  Agregar vehículo
                </Button>
              </span>
            </Tooltip>
          ) : undefined
        }
      >
        {reconciliationIssues.length > 0 && (
          <Alert
            type={hasReconciliationError ? 'error' : 'warning'}
            showIcon
            role="alert"
            message={FLEET_RECONCILIATION_MESSAGE}
            description={`Vehículos afectados: ${reconciliationIssues
              .map((vehicle) => vehicle.plate)
              .join(', ')}.`}
            style={{ marginBottom: 16 }}
          />
        )}
        <FleetVehiclesTable
          vehicles={vehicles.data}
          meta={vehicles.meta}
          loading={vehicles.loading}
          error={vehicles.error}
          canWrite={canWrite}
          fleetInactive={fleetInactive}
          onEdit={openVehicleModal}
          onDeactivate={handleDeactivateVehicle}
          onReactivate={handleReactivateVehicle}
          onRetry={vehicles.refresh}
          onOffsetChange={vehicles.setOffset}
        />
      </Card>

      <Card title="Consumo y facturación">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Disponible con US 2.9/2.5"
        />
      </Card>

      <Modal
        open={editOpen}
        title="Editar flota"
        onCancel={() => {
          setEditError(null);
          setEditOpen(false);
        }}
        footer={null}
        destroyOnHidden
        width={720}
      >
        {editError && (
          <Alert
            type="error"
            showIcon
            role="alert"
            message="No se pudo actualizar la flota"
            description={editError}
            style={{ marginBottom: 16 }}
          />
        )}
        <FleetForm
          mode="edit"
          initialValues={fleetToFormState(fleet)}
          onSubmit={handleEdit}
          submitting={editSubmitting}
        />
      </Modal>

      {fleetId && (
        <DriverFormModal
          open={driverModal.open}
          fleetId={fleetId}
          driver={driverModal.driver}
          onClose={() => setDriverModal(CLOSED_DRIVER_MODAL)}
          onSaved={() => refreshPeople('drivers')}
        />
      )}

      {fleetId && (
        <VehicleFormModal
          open={vehicleModal.open}
          fleetId={fleetId}
          vehicle={vehicleModal.vehicle}
          fleetInactive={fleetInactive}
          onClose={() => setVehicleModal(CLOSED_VEHICLE_MODAL)}
          onSaved={() => refreshPeople('vehicles')}
        />
      )}
    </div>
  );
}
